import { useState, useEffect, useCallback } from 'react';
import { adminService } from "../../apis/adminService";
import { useAuth } from "../../context/AuthContext";

export function FeedbackAuditDashboard({ onBack }) {
  const { user } = useAuth();
  const [feedbackData, setFeedbackData] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    positive: 0,
    negative: 0,
    avgEditTime: '0m',
    chartsReviewed: 0
  });
  const [loading, setLoading] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [scribeFilter, setScribeFilter] = useState('All Scribes');
  const [ratingFilter, setRatingFilter] = useState('All Ratings');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categories, setCategories] = useState([]);
  const [page, setPage] = useState(1);
  
  // Initialize limit from localStorage or default to 50
  const [limit, setLimit] = useState(() => {
    try {
      const savedLimit = localStorage.getItem('feedbackAuditPageSize');
      if (savedLimit) {
        const parsed = parseInt(savedLimit, 10);
        // Validate it's one of our allowed options
        const allowedLimits = [5, 10, 25, 50, 75, 100];
        return allowedLimits.includes(parsed) ? parsed : 50;
      }
    } catch (error) {
      console.error('Error reading from localStorage:', error);
    }
    return 50;
  });
  
  const [totalPages, setTotalPages] = useState(1);
  const [totalEntries, setTotalEntries] = useState(0);
  const [error, setError] = useState(null);
  const [showFullComments, setShowFullComments] = useState({});
  const [availableScribes, setAvailableScribes] = useState([]);
  const [dataLoaded, setDataLoaded] = useState(false);
  
  // Pagination options
  const pageSizeOptions = [
    { value: 5, label: '5 per page' },
    { value: 10, label: '10 per page' },
    { value: 25, label: '25 per page' },
    { value: 50, label: '50 per page' },
    { value: 75, label: '75 per page' },
    { value: 100, label: '100 per page' }
  ];

  // Save limit to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem('feedbackAuditPageSize', limit.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  }, [limit]);

  // Initialize on component mount
  useEffect(() => {
    const initializeData = async () => {
      try {
        setInitialLoading(true);
        setError(null);
        
        // Fetch available scribes first
        await fetchAvailableScribes();
        
        // Then fetch feedback data
        await fetchFeedbackAuditData();
        
        setDataLoaded(true);
      } catch (error) {
        console.error('Error initializing feedback audit:', error);
        setError('Failed to initialize feedback audit. Please try refreshing the page.');
      } finally {
        setInitialLoading(false);
        setLoading(false);
      }
    };

    initializeData();
  }, []);

  // Fetch data when filters, page, or limit change (but not on initial load)
  useEffect(() => {
    if (dataLoaded) {
      const timer = setTimeout(() => {
        fetchFeedbackAuditData();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [scribeFilter, ratingFilter, dateFrom, dateTo, page, limit]);

  // Reset page when filters change
  useEffect(() => {
    if (dataLoaded && page !== 1) {
      setPage(1);
    }
  }, [scribeFilter, ratingFilter, dateFrom, dateTo, limit]);

  const fetchAvailableScribes = async () => {
    try {
      const response = await adminService.getAssignableUsers();
      
      if (response && response.success) {
        const users = response.data?.users || [];

        // Sort users alphabetically by name (case‑insensitive)
      const sortedUsers = [...users].sort((a, b) => {
        const nameA = (a.name || a.email || '').toLowerCase();
        const nameB = (b.name || b.email || '').toLowerCase();
        return nameA.localeCompare(nameB);
      });


        // Create scribe options including "My Assigned" for current user
        const scribeOptions = [
          { value: 'All Scribes', label: 'All Scribes' },
          { value: 'Unassigned', label: 'Unassigned' },
          { value: 'My Assigned', label: 'My Assigned' },
          ...sortedUsers.map(user => ({
            value: user.userId || user.id,
            label: user.name || user.email || 'Unknown User'
          }))
        ];
        setAvailableScribes(scribeOptions);
      } else {
        console.warn('Failed to fetch assignable users:', response?.message);
        // Set default options
        setAvailableScribes([
          { value: 'All Scribes', label: 'All Scribes' },
          { value: 'Unassigned', label: 'Unassigned' },
          { value: 'My Assigned', label: 'My Assigned' }
        ]);
      }
    } catch (error) {
      console.error('Failed to fetch available scribes:', error);
      // Set default options on error
      setAvailableScribes([
        { value: 'All Scribes', label: 'All Scribes' },
        { value: 'Unassigned', label: 'Unassigned' },
        { value: 'My Assigned', label: 'My Assigned' }
      ]);
    }
  };

  const fetchFeedbackAuditData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Prepare filters for API call
      const filters = {
        page,
        limit
      };

      // Map scribe filter to backend parameters
      if (scribeFilter !== 'All Scribes') {
        const selectedScribe = availableScribes.find(s => s.label === scribeFilter);
        if (selectedScribe) {
          if (selectedScribe.value === 'My Assigned') {
            filters.assignedTo = 'My Assigned';
          } else if (selectedScribe.value === 'Unassigned') {
            filters.assignedTo = 'Unassigned';
          } else {
            filters.assignedTo = selectedScribe.value;
          }
        }
      }

      // Map rating filter
      if (ratingFilter !== 'All Ratings') {
        filters.rating = ratingFilter === 'Positive' ? 'positive' : 'negative';
      }

      // Date filters
      if (dateFrom) {
        filters.startDate = dateFrom;
      }

      if (dateTo) {
        filters.endDate = dateTo;
      }
      
      // Call the feedback audit endpoint
      const response = await adminService.getFeedbackAuditData(filters);

      if (response && response.success) {
        const data = response.data || {};
        
        // Ensure we have arrays for safe mapping
        const feedbacks = Array.isArray(data.feedbacks) ? data.feedbacks : [];
        const categories = Array.isArray(data.categories) ? data.categories : [];
        const stats = data.stats || {};
        const pagination = data.pagination || {};
        
        // Format feedback data for table
        const formattedFeedbacks = feedbacks.map((feedback, index) => {
          // Use the assignedTo directly from backend
          let assignedTo = feedback.assignedTo || '-';
          
          // Map 'Unassigned' to 'Unassigned' for display
          if (assignedTo === 'Unassigned' || assignedTo === 'unassigned') {
            assignedTo = 'Unassigned';
          }
          
          // Get patient name
          const patientName = feedback.patientName || 
                            (feedback.patientData?.firstName ? 
                              `${feedback.patientData?.firstName || ''} ${feedback.patientData?.lastName || ''}`.trim() : 
                              'Unknown');

          // Determine rating from type
          let rating = feedback.rating || (feedback.type === 'thumbs_up' ? 'positive' : 'negative');

          // Check if feedback is assigned to current user
          const isAssignedToMe = feedback.isAssignedToMe || false;
          
          return {
            feedbackId: feedback.feedbackId || `temp-${index}`,
            sessionId: feedback.sessionId || '-',
            patientName,
            assignedTo,
            assignedToUserId: feedback.assignedToUserId,
            rating,
            type: feedback.type || (rating === 'positive' ? 'thumbs_up' : 'thumbs_down'),
            explanation: feedback.explanation || feedback.comment || '',
            comment: feedback.explanation || feedback.comment || '',
            createdAt: feedback.createdAt || feedback.date,
            lastEdited: feedback.updatedAt || feedback.createdAt,
            user: feedback.user || 'Unknown',
            userId: feedback.userId,
            isAssignedToMe,
            rawData: feedback
          };
        });

        setFeedbackData(formattedFeedbacks);
        
        // Update stats from API response
        setStats({
          total: stats.total || formattedFeedbacks.length,
          positive: stats.positive || 0,
          negative: stats.negative || 0,
          avgEditTime: stats.avgEditTime || '0m',
          chartsReviewed: stats.chartsReviewed || 0
        });
        
        // Update categories
        if (categories.length > 0) {
          setCategories(categories);
        } else {
          // Calculate categories from negative feedback
          const negativeFeedbacks = formattedFeedbacks.filter(f => f.rating === 'negative');
          if (negativeFeedbacks.length > 0) {
            calculateCategories(negativeFeedbacks);
          } else {
            setCategories([]);
          }
        }
        
        // Update pagination
        setTotalPages(pagination.totalPages || 1);
        setTotalEntries(pagination.total || formattedFeedbacks.length);

      } else {
        console.warn('API response unsuccessful:', response);
        setError(response?.message || 'Failed to load feedback data. Please try again.');
        setFeedbackData([]);
        setCategories([]);
        setTotalEntries(0);
      }
    } catch (error) {
      console.error('Failed to fetch feedback audit data:', error);
      setError(`Error: ${error.message || 'Failed to load feedback data'}`);
      setFeedbackData([]);
      setCategories([]);
      setTotalEntries(0);
    } finally {
      setLoading(false);
    }
  };

  // Refresh function
  const handleRefresh = () => {
    setLoading(true);
    setError(null);
    fetchAvailableScribes();
    fetchFeedbackAuditData();
  };

  // Calculate categories
  const calculateCategories = (negativeFeedbacks) => {
    const totalNegative = negativeFeedbacks.length;
    
    if (totalNegative > 0) {
      // Analyze comments to categorize
      const categories = [
        { 
          name: 'Medical Accuracy', 
          count: negativeFeedbacks.filter(f => {
            const comment = f.explanation?.toLowerCase() || '';
            return comment.includes('accurate') || 
                   comment.includes('incorrect') ||
                   comment.includes('wrong') ||
                   comment.includes('error') ||
                   comment.includes('diagnosis') ||
                   comment.includes('assessment');
          }).length,
          percentage: 0
        },
        { 
          name: 'Completeness', 
          count: negativeFeedbacks.filter(f => {
            const comment = f.explanation?.toLowerCase() || '';
            return comment.includes('complete') || 
                   comment.includes('missing') ||
                   comment.includes('incomplete') ||
                   comment.includes('partial') ||
                   comment.includes('insufficient');
          }).length,
          percentage: 0
        },
        { 
          name: 'Context Missing', 
          count: negativeFeedbacks.filter(f => {
            const comment = f.explanation?.toLowerCase() || '';
            return comment.includes('context') || 
                   comment.includes('details') ||
                   comment.includes('information') ||
                   comment.includes('background') ||
                   comment.includes('history');
          }).length,
          percentage: 0
        },
        { 
          name: 'Format/Structure', 
          count: negativeFeedbacks.filter(f => {
            const comment = f.explanation?.toLowerCase() || '';
            return comment.includes('format') || 
                   comment.includes('structure') ||
                   comment.includes('organization') ||
                   comment.includes('layout') ||
                   comment.includes('style');
          }).length,
          percentage: 0
        }
      ];

      // Calculate percentages
      const totalCounted = categories.reduce((sum, cat) => sum + cat.count, 0);
      const otherCount = totalNegative - totalCounted;
      
      if (otherCount > 0) {
        categories.push({
          name: 'Other Issues',
          count: otherCount,
          percentage: 0
        });
      }

      // Calculate percentages
      categories.forEach(cat => {
        cat.percentage = Math.round((cat.count / totalNegative) * 100);
      });

      // Filter out empty categories and sort by count
      const filteredCategories = categories
        .filter(cat => cat.count > 0)
        .sort((a, b) => b.count - a.count);
      
      setCategories(filteredCategories);
    } else {
      setCategories([]);
    }
  };

  // Filter feedback based on current filters
  const filteredFeedback = feedbackData.filter(feedback => {
    // Client-side filtering as fallback
    if (scribeFilter === 'My Assigned') {
      if (!feedback.isAssignedToMe) return false;
    } else if (scribeFilter === 'Unassigned') {
      if (feedback.assignedTo !== 'Unassigned') return false;
    } else if (scribeFilter !== 'All Scribes') {
      const selectedScribe = availableScribes.find(s => s.label === scribeFilter);
      if (selectedScribe) {
        if (selectedScribe.value === 'Unassigned') {
          if (feedback.assignedTo !== 'Unassigned') return false;
        } else if (feedback.assignedTo !== selectedScribe.label) {
          return false;
        }
      }
    }
    
    if (ratingFilter === 'Positive' && feedback.rating !== 'positive') {
      return false;
    }
    
    if (ratingFilter === 'Negative' && feedback.rating !== 'negative') {
      return false;
    }
    
    // Date filtering (client-side fallback)
    if (dateFrom && feedback.createdAt) {
      try {
        const feedbackDate = new Date(feedback.createdAt);
        const fromDate = new Date(dateFrom);
        fromDate.setHours(0, 0, 0, 0);
        if (feedbackDate < fromDate) return false;
      } catch (e) {
        console.error('Error parsing dateFrom:', e);
      }
    }
    
    if (dateTo && feedback.createdAt) {
      try {
        const feedbackDate = new Date(feedback.createdAt);
        const toDate = new Date(dateTo);
        toDate.setHours(23, 59, 59, 999);
        if (feedbackDate > toDate) return false;
      } catch (e) {
        console.error('Error parsing dateTo:', e);
      }
    }
    
    return true;
  });

  // Calculate percentages for stats
  const positivePercentage = stats.total > 0 ? ((stats.positive / stats.total) * 100).toFixed(1) : '0';
  const negativePercentage = stats.total > 0 ? ((stats.negative / stats.total) * 100).toFixed(1) : '0';

  // Format feedback date
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      });
    } catch (error) {
      console.error('Error formatting date:', error, dateString);
      return '-';
    }
  };

  // Get rating display with styling
  const getRatingDisplay = (feedback) => {
    if (feedback.rating === 'positive' || feedback.type === 'thumbs_up') {
      return { 
        text: 'Positive', 
        class: 'bg-green-100 text-green-800 border-green-200',
        icon: '👍'
      };
    } else if (feedback.rating === 'negative' || feedback.type === 'thumbs_down') {
      return { 
        text: 'Negative', 
        class: 'bg-red-100 text-red-800 border-red-200',
        icon: '👎'
      };
    } else {
      return { 
        text: 'Neutral', 
        class: 'bg-gray-100 text-gray-800 border-gray-200',
        icon: '➖'
      };
    }
  };

  // Toggle full comment view
  const toggleFullComment = (feedbackId) => {
    setShowFullComments(prev => ({
      ...prev,
      [feedbackId]: !prev[feedbackId]
    }));
  };

  // Reset all filters
  const handleResetFilters = () => {
    setScribeFilter('All Scribes');
    setRatingFilter('All Ratings');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  // Handle page size change
  const handlePageSizeChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  // Export data to CSV
  const handleExportCSV = () => {
    if (filteredFeedback.length === 0) {
      alert('No data to export');
      return;
    }
    
    try {
      const csvContent = [
        ['Session ID', 'Patient', 'Assigned To', 'Rating', 'Comment', 'Date', 'Feedback By', 'User ID'],
        ...filteredFeedback.map(feedback => {
          const rating = getRatingDisplay(feedback);
          return [
            feedback.sessionId || '-',
            feedback.patientName || '-',
            feedback.assignedTo === '-' ? 'Unassigned' : feedback.assignedTo || '-',
            rating.text,
            `"${(feedback.explanation || '').replace(/"/g, '""')}"`,
            formatDate(feedback.createdAt || feedback.lastEdited),
            feedback.user || '-',
            feedback.userId || '-'
          ];
        })
      ].map(row => row.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `feedback-audit-${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      alert(`Exported ${filteredFeedback.length} feedback entries to CSV`);
    } catch (error) {
      console.error('Error exporting CSV:', error);
      alert('Failed to export CSV. Please try again.');
    }
  };

  // Generate page numbers for pagination
  const generatePageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(1, page - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages, start + maxVisible - 1);
      
      if (end - start + 1 < maxVisible) {
        start = Math.max(1, end - maxVisible + 1);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }
    
    return pages;
  };

  // Calculate time ago for feedback
  const getTimeAgo = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      
      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
      return `${Math.floor(diffDays / 365)} years ago`;
    } catch (error) {
      return '';
    }
  };

  // Show loading screen for initial load
  if (initialLoading) {
    return (
      <div className="min-h-screen bg-gray-50 p-4 sm:p-6 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-gray-600">Loading feedback audit dashboard...</div>
          <div className="text-sm text-gray-400 mt-2">Please wait while we load your data</div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 w-full">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Feedback & Audit Dashboard</h1>
              <p className="text-gray-600 mt-1">
                Monitor AI performance and scribe feedback patterns
                {user && <span className="text-blue-600 ml-2">• Logged in as: {user.email}</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              {/* Refresh Button */}
              <button
                onClick={handleRefresh}
                disabled={loading}
                className="flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <svg 
                  className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} 
                  fill="none" 
                  viewBox="0 0 24 24" 
                  stroke="currentColor"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" 
                  />
                </svg>
                {loading ? 'Refreshing...' : 'Refresh'}
              </button>
              
              <button 
                onClick={onBack}
                className="flex items-center text-blue-600 hover:text-blue-800 group px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <span className="mr-2">←</span>
                <span className="text-sm font-medium">Back to Dashboard</span>
              </button>
            </div>
          </div>

          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span>{error}</span>
              </div>
            </div>
          )}

          {loading && (
            <div className="flex justify-center items-center mb-6">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              <span className="ml-3 text-gray-600">Loading feedback data...</span>
            </div>
          )}

          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">Total Feedback</div>
              <div className="text-2xl font-bold text-gray-900">{stats.total}</div>
              <div className="text-xs text-gray-500 mt-1">all time</div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">Positive Rating</div>
              <div className="text-2xl font-bold text-gray-900">{stats.positive}</div>
              <div className="text-xs text-green-600 font-medium mt-1">{positivePercentage}% positive</div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">Negative Rating</div>
              <div className="text-2xl font-bold text-gray-900">{stats.negative}</div>
              <div className="text-xs text-red-600 font-medium mt-1">{negativePercentage}% negative</div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">Avg Edit Time</div>
              <div className="text-2xl font-bold text-gray-900">{stats.avgEditTime}</div>
              <div className="text-xs text-gray-500 mt-1">per session</div>
            </div>
            <div className="bg-white rounded-xl shadow p-6 border border-gray-200">
              <div className="text-xs font-medium text-gray-500 mb-2">Charts Reviewed</div>
              <div className="text-2xl font-bold text-gray-900">{stats.chartsReviewed}</div>
              <div className="text-xs text-gray-500 mt-1">total finalized</div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="bg-white rounded-xl shadow mb-6 border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Filters</h3>
              <div className="flex gap-3">
                <button 
                  onClick={handleResetFilters}
                  className="px-3 py-2 border border-gray-300 bg-white text-gray-700 rounded-lg text-xs hover:bg-gray-50 transition-colors font-medium"
                  disabled={loading}
                >
                  Reset Filters
                </button>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Scribe Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Assigned Scribe
                </label>
                <select 
                  value={scribeFilter}
                  onChange={(e) => setScribeFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={loading}
                >
                  {availableScribes.map(scribe => (
                    <option key={scribe.value} value={scribe.label}>
                      {scribe.label}
                    </option>
                  ))}
                </select>
                {scribeFilter === 'My Assigned' && user && (
                  <p className="text-xs text-blue-600 mt-1">
                    Showing feedback for SOAP notes assigned to you
                  </p>
                )}
              </div>

              {/* Rating Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Rating
                </label>
                <select 
                  value={ratingFilter}
                  onChange={(e) => setRatingFilter(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  disabled={loading}
                >
                  <option value="All Ratings">All Ratings</option>
                  <option value="Positive">Positive</option>
                  <option value="Negative">Negative</option>
                </select>
              </div>

              {/* Date Range Filter */}
              <div className="space-y-2">
                <label className="block text-sm font-medium text-gray-700">
                  Date Range
                </label>
                <div className="flex items-center space-x-2">
                  <input 
                    type="date" 
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  />
                  <span className="text-sm text-gray-500">to</span>
                  <input 
                    type="date" 
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>

            {/* Pagination Info and Controls */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    Showing <span className="font-medium">{(page - 1) * limit + 1}</span> to{' '}
                    <span className="font-medium">{Math.min(page * limit, totalEntries)}</span> of{' '}
                    <span className="font-medium">{totalEntries}</span> total entries
                  </div>
                  
                  {/* Page Size Selector */}
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Show:</label>
                    <select 
                      value={limit}
                      onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                      className="px-2 py-1 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      disabled={loading}
                    >
                      {pageSizeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {/* Pagination Controls */}
                {totalPages > 1 && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => handlePageChange(page - 1)}
                      disabled={page === 1 || loading}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      ← Previous
                    </button>
                    <div className="text-sm text-gray-600">
                      Page <span className="font-medium">{page}</span> of{' '}
                      <span className="font-medium">{totalPages}</span>
                    </div>
                    <button
                      onClick={() => handlePageChange(page + 1)}
                      disabled={page === totalPages || loading}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next →
                    </button>
                  </div>
                )}
              </div>
              
              {/* Current Filters Info */}
              <div className="mt-2 text-xs text-gray-500">
                Current filters: 
                <span className="ml-1 font-medium">
                  {scribeFilter} • {ratingFilter}
                  {dateFrom && ` • From: ${dateFrom}`}
                  {dateTo && ` • To: ${dateTo}`}
                  {` • ${limit} per page`}
                </span>
              </div>
            </div>
          </div>

          {/* Feedback Table */}
          <div className="bg-white rounded-xl shadow border border-gray-200">
            <div className="bg-gray-50 border-b border-gray-200">
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Feedback Entries ({filteredFeedback.length})
                </h3>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Session ID</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Patient</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Assigned Scribe</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Rating</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Comment</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Feedback By</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center">
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-2"></div>
                          <div className="text-gray-500">Loading feedback data...</div>
                        </div>
                      </td>
                    </tr>
                  ) : filteredFeedback.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="p-8 text-center">
                        <div className="text-gray-500 mb-2">
                          {feedbackData.length === 0 
                            ? 'No feedback data available' 
                            : 'No feedback matches the current filters'
                          }
                        </div>
                        {feedbackData.length > 0 && (
                          <button 
                            onClick={handleResetFilters}
                            className="text-sm text-blue-600 hover:text-blue-800 hover:underline font-medium"
                          >
                            Reset filters to show all feedback
                          </button>
                        )}
                      </td>
                    </tr>
                  ) : (
                    filteredFeedback.map((feedback, index) => {
                      const rating = getRatingDisplay(feedback);
                      const comment = feedback.explanation || feedback.comment || '';
                      const isFullCommentVisible = showFullComments[feedback.feedbackId];
                      const isLongComment = comment.length > 100;
                      
                      return (
                        <tr 
                          key={feedback.feedbackId || index} 
                          className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                            feedback.isAssignedToMe ? 'bg-blue-50 hover:bg-blue-100' : ''
                          }`}
                        >
                          <td className="p-4">
                            <div className="text-sm font-mono text-gray-700">
                              {feedback.sessionId || '-'}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm font-medium text-gray-900">
                              {feedback.patientName || '-'}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-gray-700">
                              {feedback.assignedTo === 'Unassigned' ? (
                                <span className="text-gray-400 italic">Unassigned</span>
                              ) : (
                                <div className="flex items-center">
                                  <span>{feedback.assignedTo || '-'}</span>
                                  {feedback.isAssignedToMe && (
                                    <span className="ml-2 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                      You
                                    </span>
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="p-4">
                            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border ${rating.class}`}>
                              <span className="mr-1">{rating.icon}</span>
                              {rating.text}
                            </span>
                          </td>
                          <td className="p-4 max-w-xs">
                            <div className="text-sm text-gray-700">
                              {isFullCommentVisible ? comment : (isLongComment ? `${comment.substring(0, 100)}...` : comment)}
                            </div>
                            {isLongComment && comment.length > 100 && (
                              <button
                                onClick={() => toggleFullComment(feedback.feedbackId)}
                                className="mt-1 text-xs text-blue-600 hover:text-blue-800 hover:underline"
                              >
                                {isFullCommentVisible ? 'Show less' : 'Show more'}
                              </button>
                            )}
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-gray-600">
                              {formatDate(feedback.createdAt || feedback.lastEdited)}
                            </div>
                            <div className="text-xs text-gray-400">
                              {getTimeAgo(feedback.createdAt || feedback.lastEdited)}
                            </div>
                          </td>
                          <td className="p-4">
                            <div className="text-sm text-gray-700">
                              {feedback.user || '-'}
                            </div>
                            {feedback.userId && (
                              <div className="text-xs text-gray-400 font-mono">
                                {feedback.userId.substring(0, 8)}...
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Common Issues Section */}
          {categories.length > 0 && (
            <div className="bg-white rounded-xl shadow mb-6 border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">Common Issues Identified</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Top categories from {stats.negative} negative feedback entries
                    </p>
                  </div>
                  <div className="text-sm text-gray-500">
                    Based on feedback analysis
                  </div>
                </div>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {categories.map((category, idx) => (
                    <div key={idx} className="bg-gray-50 border border-gray-200 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-sm font-medium text-gray-900">{category.name}</div>
                        <div className="text-xs text-gray-500">{category.count} occurrences</div>
                      </div>
                      <div className="text-2xl font-bold text-gray-900 mb-1">{category.percentage}%</div>
                      <div className="text-xs text-gray-600 mb-2">of negative feedback</div>
                      <div className="mt-2">
                        <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-red-500 rounded-full transition-all duration-300"
                            style={{ width: `${category.percentage}%` }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                {stats.negative > 0 && (
                  <div className="mt-4 text-sm text-gray-600">
                    <p>
                      Analysis shows {categories.reduce((sum, cat) => sum + cat.count, 0)} specific issues 
                      identified across {stats.negative} negative feedback entries.
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Export Options */}
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button 
              onClick={handleExportCSV}
              disabled={filteredFeedback.length === 0 || loading}
              className={`px-6 py-3 border border-gray-300 rounded-lg text-sm font-medium transition-colors ${
                filteredFeedback.length === 0 || loading
                  ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                  : 'bg-white hover:bg-gray-50 text-gray-700'
              }`}
            >
              Export as CSV
            </button>
            <button 
              onClick={() => {
                // Generate report implementation
                alert('Report generation feature coming soon!');
              }}
              disabled={filteredFeedback.length === 0 || loading}
              className={`px-6 py-3 rounded-lg text-sm font-medium transition-colors ${
                filteredFeedback.length === 0 || loading
                  ? 'bg-gray-400 text-gray-200 cursor-not-allowed' 
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              Generate Report
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}